/**
 * 非顏色的設計 token：字體、字級、圓角、間距、動畫。
 *
 * 數值依照 Material Design 3 規格。web 與 app 兩端都讀這裡，
 * 不要在各自的 app 裡另外寫死。
 */

/**
 * 字體。
 *
 * Roboto 沒有中日韓字符，所以中文一定要有 Noto Sans TC 接在後面，
 * 否則會 fallback 到系統字型，不同電腦看起來會不一樣。
 * 兩者都用 @fontsource 從本地載入，不依賴 Google Fonts CDN
 * （內部系統不該假設有外網）。
 */
export const fontFamily = {
  sans: '"Roboto", "Noto Sans TC", "Helvetica Neue", Arial, sans-serif',
  mono: '"Roboto Mono", "Noto Sans TC", ui-monospace, monospace',
} as const;

export interface TypographyStyle {
  fontSize: number;
  lineHeight: number;
  fontWeight: number;
  letterSpacing: number;
}

/**
 * 字級系統。
 *
 * 級距（fontSize / lineHeight）沿用 MD3，但兩件事刻意偏離規格：
 *
 * 1. **字距收緊。** MD3 給 body 與 label 正的 letterSpacing（+0.5 / +0.25），
 *    那是為了 Roboto 在小尺寸下的可讀性；標題級以上收負值則是中性設計語言
 *    的一部分 —— 大字疏鬆會顯得鬆散。中文本身不需要額外字距。
 * 2. **字重對比拉大。** 標題 700、標籤與按鈕 600、正文 400。
 *    Roboto 與 Noto Sans TC 都有真正的 600 字面（見 AppThemeProvider 的
 *    @fontsource import），所以不會是瀏覽器合成出來的假粗體。
 *
 * 單位為 px；letterSpacing 也是 px，由 muiTheme 換算成 em。
 */
export const typeScale = {
  displayLarge: { fontSize: 57, lineHeight: 64, fontWeight: 700, letterSpacing: -1.43 },
  displayMedium: { fontSize: 45, lineHeight: 52, fontWeight: 700, letterSpacing: -1.08 },
  displaySmall: { fontSize: 36, lineHeight: 44, fontWeight: 700, letterSpacing: -0.86 },

  headlineLarge: { fontSize: 32, lineHeight: 40, fontWeight: 700, letterSpacing: -0.77 },
  headlineMedium: { fontSize: 28, lineHeight: 34, fontWeight: 700, letterSpacing: -0.62 },
  headlineSmall: { fontSize: 24, lineHeight: 30, fontWeight: 700, letterSpacing: -0.48 },

  titleLarge: { fontSize: 20, lineHeight: 26, fontWeight: 700, letterSpacing: -0.28 },
  titleMedium: { fontSize: 15, lineHeight: 20, fontWeight: 600, letterSpacing: -0.12 },
  titleSmall: { fontSize: 13.5, lineHeight: 18, fontWeight: 600, letterSpacing: -0.11 },

  bodyLarge: { fontSize: 15, lineHeight: 22, fontWeight: 400, letterSpacing: 0 },
  bodyMedium: { fontSize: 14, lineHeight: 20, fontWeight: 400, letterSpacing: 0 },
  bodySmall: { fontSize: 12.5, lineHeight: 17, fontWeight: 400, letterSpacing: 0 },

  labelLarge: { fontSize: 13.5, lineHeight: 18, fontWeight: 600, letterSpacing: 0 },
  labelMedium: { fontSize: 12.5, lineHeight: 16, fontWeight: 600, letterSpacing: 0 },
  labelSmall: { fontSize: 12, lineHeight: 16, fontWeight: 600, letterSpacing: 0.24 },
} as const satisfies Record<string, TypographyStyle>;

export type TypeScaleKey = keyof typeof typeScale;

/**
 * 圓角。鍵名沿用 MD3，數值整體加大以符合中性設計語言。
 *
 * `shell` 是額外新增的一階，給最外層的兩張卡片（服務 rail 與主內容卡）用；
 * MD3 沒有對應的角色，但那個尺度確實需要一個名字，否則會散落成寫死的 24。
 * `full` 用於 pill 形狀的按鈕與 chip。
 */
export const shape = {
  none: 0,
  extraSmall: 6,
  small: 10,
  medium: 14,
  large: 20,
  shell: 24,
  extraLarge: 28,
  full: 9999,
} as const;

/**
 * 間距。MD3 用 4dp 網格，但 MUI 的 spacing(1) 預設是 8px，
 * 所以基準取 8、另外提供 4 的倍數供需要更細的地方使用。
 */
export const spacing = {
  base: 8,
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;

/** 斷點。與 MD3 的 window size class 對齊。 */
export const breakpoints = {
  compact: 0, // 手機
  medium: 600, // 直立平板 / 折疊機展開
  expanded: 840, // 橫向平板
  large: 1200, // 桌機
  extraLarge: 1600,
} as const;

/** 動畫時長與曲線（MD3 motion）。 */
export const motion = {
  duration: {
    short: 100,
    medium: 250,
    long: 400,
  },
  easing: {
    standard: "cubic-bezier(0.2, 0, 0, 1)",
    emphasized: "cubic-bezier(0.2, 0, 0, 1)",
    decelerated: "cubic-bezier(0, 0, 0, 1)",
    accelerated: "cubic-bezier(0.3, 0, 1, 1)",
  },
} as const;

/**
 * MD3 的表面層級對應到哪個色彩角色。
 *
 * MD3 不用陰影表達高度，改用「表面色越高層越亮」。Paper 的
 * elevation.level0~5 與 MUI 的 Paper/Card 背景都照這張表取色。
 */
/**
 * 版面密度。
 *
 * 這些數字散落在各處會很難統一調整（列高改一次要動 DataGrid、骨架屏、
 * 空狀態三個地方），所以集中在這裡。
 *
 * `rowHeight` 48 是刻意選的：一列要放得下「編號（小灰字）+ 名稱（粗體）」
 * 兩行 —— 16 + 20 = 36px 的文字，加上 6px 上下留白剛好 48。再低就只能單行。
 */
export const density = {
  /** 表格列高。DataGrid 的 rowHeight 與骨架屏都取這個值 */
  rowHeight: 48,
  /** 表格欄位標題列的高度 */
  headerHeight: 40,
  /** 服務 rail 的寬度（只有圖示時） */
  railWidth: 68,
  /** rail 裡每個圖示按鈕的尺寸 */
  railItemSize: 44,
  /** rail 展開後第二層的寬度 */
  navPanelWidth: 240,
  /** 主內容卡片頂列的高度 */
  topBarHeight: 64,
  /** 最外層灰底與卡片之間的留白 */
  shellGap: 12,
  /** 手機版底部懸浮導覽列的高度（不含它與畫面底緣之間的留白） */
  tabBarHeight: 62,
} as const;

export const elevationSurface = [
  "surface",
  "surfaceContainerLow",
  "surfaceContainer",
  "surfaceContainerHigh",
  "surfaceContainerHigh",
  "surfaceContainerHighest",
] as const;
